'use client'

import { useState, useCallback, useMemo } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Search,
  X,
  Globe,
  Map,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  DollarSign,
  Wifi,
} from 'lucide-react'
import { CompetitorPlan as CompetitorPrice, Match as ComparisonItem, ComparisonData } from '@/types/esim'
import { useEsimCompareQuery as useEsimComparison } from '@/hooks/use-esim'
import { useRouter } from 'next/navigation'
import { setEsimSelection } from '@/lib/esim-selection'

type Currency   = 'USD' | 'AUD'
type TargetType = 'all' | 'countries' | 'regions'

function fmt(price: number): string {
  return `$${price.toFixed(2)}`
}

function savingsPct(ours: number, theirs: number): number | null {
  if (!theirs || theirs <= ours) return null
  return Math.round(((theirs - ours) / theirs) * 100)
}

function VellPrice({ item }: { item: ComparisonItem }) {
  const memberPrice = item.our_plan.membership_price
  const guestPrice  = item.our_plan.price

  if (!memberPrice) {
    return (
      <span className="text-base font-bold text-brand-text tabular-nums 3xl:text-xl">
        {fmt(guestPrice)}
      </span>
    )
  }

  return (
    <div className="flex flex-col items-center gap-0.5">
      <div className="flex items-center gap-1.5">
        <span className="text-base font-bold text-brand-text tabular-nums 3xl:text-xl">
          {fmt(memberPrice)}
        </span>
      </div>
    </div>
  )
}

function CompCell({ comp, ourPrice }: {
  comp: CompetitorPrice | null
  ourPrice: number
}) {
  if (!comp) {
    return (
      <div className="flex flex-col items-center gap-1">
        <span className="text-sm text-brand-text/70 3xl:text-base">Not available</span>
      </div>
    )
  }

  const pct = savingsPct(ourPrice, comp.price)
  const isCheaper = pct !== null && pct > 0

  return (
    <div className="flex flex-col items-center gap-1">
      <span className={[
        'text-base font-semibold tabular-nums 3xl:text-xl',
        isCheaper ? 'text-brand-muted' : 'text-brand-muted',
      ].join(' ')}>
        {fmt(comp.price)}
      </span>
    </div>
  )
}

function SkeletonRows() {
  return (
    <>
      <div className="hidden sm:block">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="grid items-center border-b border-brand-stroke/40 last:border-0 animate-pulse"
            style={{ gridTemplateColumns: '1fr 180px 150px 150px 130px' }}
          >
            <div className="px-5 py-4">
              <div className="h-4 w-32 bg-brand-stroke/40 rounded mb-2" />
              <div className="h-3 w-20 bg-brand-stroke/20 rounded" />
            </div>
            {[1, 2, 3, 4].map((j) => (
              <div key={j} className="px-5 py-4 flex justify-center">
                <div className="h-5 w-16 bg-brand-stroke/30 rounded" />
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="sm:hidden flex flex-col gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="global-border-radius border border-brand-stroke bg-brand-card p-4 animate-pulse">
            <div className="h-4 w-28 bg-brand-stroke/40 rounded mb-3" />
            <div className="grid grid-cols-3 gap-3">
              {[1, 2, 3].map((j) => (
                <div key={j} className="h-10 bg-brand-stroke/20 global-border-radius" />
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  )
}

function DesktopRow({ item, i, isLast }: {
  item: ComparisonItem
  i: number
  isLast: boolean
}) {
  const ourPrice = item.our_plan.membership_price ?? item.our_plan.price
  const router = useRouter()
  const handleChoose = () => {
    setEsimSelection(item.our_plan.slug)
    router.push(`/travel-esim/${item.target.label.toLowerCase().replace(/\s+/g, '-')}`)
  }

  const vellCellClass = [
    'px-5 py-4 bg-brand-card/90 relative',
    'before:absolute before:inset-0',
    'before:border-l-2 before:border-r-2 before:border-brand-grey',
    'before:pointer-events-none',
    isLast ? 'before:border-b-2 before:rounded-bl-2xl before:rounded-br-2xl' : '',
  ].join(' ')

  return (
    <motion.tr
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.3, delay: i * 0.06 }}
      className="border-b border-brand-stroke last:border-0 hover:bg-brand-surface/30 transition-colors"
    >
      <td className="px-5 py-4">
        <p className="text-base font-semibold text-brand-text 3xl:text-2xl">{item.target.label}</p>
        <div className="flex items-center gap-1.5 mt-0.5 text-xs text-brand-muted 3xl:text-xl">
          <span>{item.target.duration}</span>
          <span className="text-brand-stroke">·</span>
          <span>{item.target.unlimited ? 'Unlimited' : `${item.target.data_quota_gb} GB`}</span>
        </div>
      </td>
      <td className={vellCellClass}>
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-center gap-1.5">
            <VellPrice item={item} />
          </div>
        </div>
      </td>
      <td className="px-5 py-4">
        <CompCell comp={item.airalo ?? null} ourPrice={ourPrice} />
      </td>
      <td className="px-5 py-4">
        <CompCell comp={item.holafly ?? null} ourPrice={ourPrice} />
      </td>
      <td className="px-4 py-4 text-right">
        <button
          onClick={handleChoose}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 global-border-radius text-xs font-semibold bg-brand-grey border border-brand-card text-brand-card hover:bg-brand-grey/70 transition-colors 3xl:text-xl"
        >
          <Wifi className="h-3.5 w-3.5" />
          Choose Plan
        </button>
      </td>
    </motion.tr>
  )
}

function MobileCard({ item, i }: {
  item: ComparisonItem
  i: number
}) {
  const ourPrice = item.our_plan.membership_price ?? item.our_plan.price
  const router = useRouter()
  const handleChoose = () => {
    setEsimSelection(item.our_plan.slug)
    router.push(`/travel-esim/${item.target.label.toLowerCase().replace(/\s+/g, '-')}`)
  }
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.28, delay: i * 0.04 }}
      className="global-border-radius border border-brand-stroke bg-brand-card overflow-hidden"
    >
      <div className="px-4 py-3 border-b border-brand-stroke bg-brand-surface/30 flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-sm font-bold text-brand-text">{item.target.label}</span>
          </div>
          <p className="text-[11px] text-brand-muted mt-0.5">
            {item.target.unlimited ? 'Unlimited' : `${item.target.data_quota_gb} GB`}
            <span className="mx-1 text-brand-stroke">·</span>
            {item.target.duration}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-3 divide-x divide-brand-stroke/60">
        <div className="px-3 py-3.5 bg-brand-card flex flex-col items-center gap-2">
          <Image src="/logo-tr.png" alt="Vell" width={36} height={12} className="h-3 w-auto mb-0.5" />
          <VellPrice item={item} />
        </div>
        <div className="px-3 py-3.5 flex flex-col items-center gap-2">
          <Image src="/airalo.png" alt="Airalo" width={40} height={12} className="h-3 w-auto mb-0.5" />
          <CompCell comp={item.airalo ?? null} ourPrice={ourPrice} />
        </div>
        <div className="px-3 py-3.5 flex flex-col items-center gap-2">
          <Image src="/holafly.svg" alt="Holafly" width={44} height={12} className="h-3 w-auto mb-0.5" />
          <CompCell comp={item.holafly ?? null} ourPrice={ourPrice} />
        </div>
      </div>
      <div className="px-4 py-3 border-t border-brand-stroke/60 bg-brand-surface/20">
        <button
          onClick={handleChoose}
          className="flex items-center justify-center gap-1.5 w-full py-2 global-border-radius text-xs font-bold bg-brand-grey text-brand-card hover:bg-brand-grey/80 transition-colors"
        >
          <Wifi className="h-3.5 w-3.5" />
          Choose Plan
        </button>
      </div>
    </motion.div>
  )
}

const PAGE_SIZE = 10

function applyCompetitorFilter(matches: ComparisonItem[]): ComparisonItem[] {
  return matches.filter((item) => {
    const ourPrice   = item.our_plan.membership_price ?? item.our_plan.price
    const beatsAiralo  = item.airalo  ? savingsPct(ourPrice, item.airalo.price)  : null
    const beatsHolafly = item.holafly ? savingsPct(ourPrice, item.holafly.price) : null
    return beatsAiralo || beatsHolafly
  })
}

export default function ComparisonContent({ initialData }: { initialData: ComparisonData | null }) {
  const [search,     setSearch]     = useState('')
  const [targetType, setTargetType] = useState<TargetType>('countries')
  const [currency,   setCurrency]   = useState<Currency>('AUD')
  const [page,       setPage]       = useState(1)
  const [hasInteracted, setHasInteracted] = useState(false)

  const resetPage = useCallback(() => setPage(1), [])
  const interact  = useCallback(() => setHasInteracted(true), [])

  const scope = targetType === 'regions' ? 'regions' : 'countries'

  // Only enable React Query once user has interacted or there's no server data
  const isQueryEnabled = hasInteracted || !initialData

  const { data: queryData, isLoading } = useEsimComparison(
    isQueryEnabled ? scope : '',
    isQueryEnabled ? currency : undefined,
  )

  const allItems: ComparisonItem[] = isQueryEnabled
    ? (queryData?.matches ?? [])
    : (initialData?.matches ?? [])

  const currentlyLoading = isQueryEnabled && isLoading

  const filteredItems = useMemo(() => {
    let result = applyCompetitorFilter(allItems)
    if (search) {
      const aliases: Record<string, string[]> = {
        'united kingdom': ['uk', 'england', 'scotland', 'wales'],
      }
      const q = search.toLowerCase()
      result = result.filter((item) => {
        const label = item.target.label.toLowerCase()
        if (label.includes(q)) return true
        const alts = aliases[label]
        return alts ? alts.some((a) => a.includes(q) || q.includes(a)) : false
      })
    }
    return result
  }, [allItems, search])

  const totalPages = Math.ceil(filteredItems.length / PAGE_SIZE)
  const pageItems  = filteredItems.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  return (
    <div className="min-h-screen bg-brand-bg text-brand-text">
      <section className="relative overflow-hidden bg-brand-bg 3xl:max-w-[1432px] mx-auto">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.018]"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,1) 1px, transparent 0)',
            backgroundSize: '36px 36px',
          }}
        />
        <div className="relative z-10 mx-auto max-w-5xl px-6 py-5 sm:py-20 xl:py-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col items-center gap-5"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.06] 3xl:text-8xl">
              Vell eSIM vs{' '}
              <span
                className="text-transparent bg-clip-text"
                style={{ backgroundImage: 'linear-gradient(135deg, #23EBAD 0%, #008080 100%)' }}
              >
                The Competition
              </span>
            </h1>
            <p className="text-brand-muted text-base sm:text-lg max-w-xl leading-relaxed 3xl:text-2xl">
              Same data. Same duration. Better price — across {filteredItems.length || 'hundreds of'} destinations worldwide.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="sticky top-0 z-20 bg-brand-bg/90 backdrop-blur-md">
        <div className="mx-auto max-w-5xl px-6 py-3 flex flex-col sm:flex-row gap-3 3xl:max-w-[1432px] mx-auto">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-muted pointer-events-none" />
            <input
              value={search}
              onChange={(e) => { interact(); setSearch(e.target.value); resetPage() }}
              placeholder="Search by country or region…"
              className="w-full pl-10 pr-9 py-2.5 text-sm bg-brand-card border border-brand-stroke rounded-full text-brand-text placeholder:text-brand-muted focus:outline-none focus:border-brand-green/50 transition-colors 3xl:text-xl"
            />
            {search && (
              <button
                onClick={() => { setSearch(''); resetPage() }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-muted hover:text-brand-text transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="flex items-center gap-0.5 p-1 global-border-radius bg-brand-card border border-brand-stroke">
              {([
                { key: 'countries', icon: Globe },
                { key: 'regions',   icon: Map  },
              ] as const).map(({ key, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => { interact(); setTargetType(key); resetPage() }}
                  className={[
                    'flex items-center px-3 py-2 global-border-radius text-xs font-semibold transition-all duration-200 capitalize 3xl:text-xl',
                    targetType === key
                      ? 'bg-brand-grey text-brand-bg shadow-sm'
                      : 'text-brand-muted hover:text-brand-text',
                  ].join(' ')}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {key}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-0.5 p-1 global-border-radius bg-brand-card border border-brand-stroke">
              {(['AUD', 'USD'] as Currency[]).map((c) => (
                <button
                  key={c}
                  onClick={() => { interact(); setCurrency(c); resetPage() }}
                  className={[
                    'flex items-center gap-1 px-3 py-2 global-border-radius text-xs font-bold transition-all duration-200 3xl:text-xl',
                    currency === c
                      ? 'bg-brand-grey text-brand-bg shadow-sm'
                      : 'text-brand-muted hover:text-brand-text',
                  ].join(' ')}
                >
                  <DollarSign className="h-3.5 w-3.5" />
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-6 py-8 flex flex-col gap-6 3xl:max-w-[1432px]">
        {!currentlyLoading && filteredItems.length > 0 && (
          <p className="text-xs text-brand-muted 3xl:text-xl">
            Showing{' '}
            <span className="font-semibold text-brand-text">
              {Math.min((page - 1) * PAGE_SIZE + 1, filteredItems.length)}–{Math.min(page * PAGE_SIZE, filteredItems.length)}
            </span>{' '}
            of{' '}
            <span className="font-semibold text-brand-text">{filteredItems.length}</span> plans
            {search && <> matching <span className="text-brand-green font-medium">&quot;{search}&quot;</span></>}
          </p>
        )}

        <div className="hidden lg:block global-border-radius border border-brand-stroke overflow-hidden bg-brand-card">
          {currentlyLoading ? (
            <SkeletonRows />
          ) : pageItems.length > 0 && (
            <table className="w-full border-collapse" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '160px' }} />
                <col style={{ width: '160px' }} />
                <col style={{ width: '130px' }} />
                <col style={{ width: '130px' }} />
                <col style={{ width: '110px' }} />
              </colgroup>
              <thead>
                <tr className="border-b-2 border-brand-stroke bg-brand-surface/60">
                  <th className="px-5 py-3.5 text-left">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-brand-muted 3xl:text-base">
                      Destination
                    </span>
                  </th>
                  <th className={[
                    'px-5 py-3.5 text-center bg-transparent relative',
                    'before:absolute before:inset-0',
                    'before:border-l-2 before:border-r-2 before:border-t-2',
                    'before:border-brand-grey before:rounded-tl-2xl before:rounded-tr-2xl',
                    'before:pointer-events-none',
                  ].join(' ')}>
                    <div className="flex justify-center">
                      <Image src="/logo-tr.png" alt="Vell" width={52} height={16} className="h-4 w-auto" />
                    </div>
                  </th>
                  <th className="px-5 py-3.5">
                    <div className="flex justify-center">
                      <Image src="/airalo.png" alt="Airalo" width={52} height={16} className="h-4 w-auto" />
                    </div>
                  </th>
                  <th className="px-5 py-3.5">
                    <div className="flex justify-center">
                      <Image src="/holafly.svg" alt="Holafly" width={60} height={16} className="h-4 w-auto" />
                    </div>
                  </th>
                  <th className="px-5 py-3.5" />
                </tr>
              </thead>
              <AnimatePresence mode="wait">
                <motion.tbody key={`${page}-${scope}-${search}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
                  {pageItems.map((item, i) => (
                    <DesktopRow key={item.id} item={item} i={i} isLast={i === pageItems.length - 1} />
                  ))}
                </motion.tbody>
              </AnimatePresence>
            </table>
          )}
        </div>

        <div className="lg:hidden flex flex-col gap-3">
          {currentlyLoading ? (
            <SkeletonRows />
          ) : (
            <AnimatePresence mode="wait">
              <motion.div key={`mob-${page}-${scope}-${search}`} className="flex flex-col gap-3" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
                {pageItems.map((item, i) => (
                  <MobileCard key={item.id} item={item} i={i} />
                ))}
              </motion.div>
            </AnimatePresence>
          )}
        </div>

        {!currentlyLoading && pageItems.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-3 py-20 rounded-full border border-brand-stroke bg-brand-card">
            <Search className="h-8 w-8 text-brand-stroke" />
            <p className="text-sm text-brand-muted 3xl:text-xl">No plans match your search.</p>
            <button
              onClick={() => { setSearch(''); setTargetType('countries'); resetPage() }}
              className="text-xs text-brand-green underline underline-offset-2 hover:opacity-80 transition-opacity"
            >
              Clear filters
            </button>
          </div>
        )}

        {!currentlyLoading && totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2 border-t border-brand-stroke/40">
            <button
              type="button"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              className="flex items-center gap-2 w-full sm:w-auto justify-center px-4 py-2.5 global-border-radius border border-brand-stroke bg-brand-card text-sm 3xl:text-xl font-semibold text-brand-muted hover:text-brand-text hover:border-brand-green/30 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </button>
            <div className="flex flex-wrap items-center justify-center gap-1.5">
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                let p: number
                if (totalPages <= 7)             p = i + 1
                else if (page <= 4)              p = i + 1
                else if (page >= totalPages - 3) p = totalPages - 6 + i
                else                             p = page - 3 + i
                return (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={[
                      'min-w-[2.25rem] h-9 global-border-radius text-xs 3xl:text-xl font-bold transition-all duration-150',
                      p === page
                        ? 'bg-brand-grey text-brand-bg shadow-sm'
                        : 'text-brand-muted hover:text-brand-text hover:bg-brand-surface',
                    ].join(' ')}
                  >
                    {p}
                  </button>
                )
              })}
            </div>
            <button
              type="button"
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="flex items-center gap-2 w-full sm:w-auto justify-center px-4 py-2.5 global-border-radius border border-brand-stroke bg-brand-card text-sm 3xl:text-xl font-semibold text-brand-muted hover:text-brand-text hover:border-brand-green/30 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}

        <p className="text-sm 3xl:text-xl text-brand-muted/40 text-center max-w-lg 3xl:max-w-2xl mx-auto">
          Competitor prices are estimates based on publicly available data and may vary.
          Vell member prices require a free account. Guest rates apply otherwise.
        </p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="rounded-3xl bg-gradient-to-br from-brand-green/10 via-brand-card to-brand-purple/6 p-10 text-center relative overflow-hidden border border-brand-green/15"
        >
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.018]"
            style={{
              backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,1) 1px, transparent 0)',
              backgroundSize: '36px 36px',
            }}
          />
          <div className="relative z-10 flex flex-col items-center gap-4">
            <h2 className="text-2xl font-bold text-brand-text tracking-tight 3xl:text-[3rem]">
              Ready to save on your next trip?
            </h2>
            <p className="text-brand-muted text-sm 3xl:text-2xl max-w-md 3xl:max-w-2xl leading-relaxed">
              Instant eSIM delivery, better prices, no physical SIM card required.
            </p>
            <div className="flex flex-wrap justify-center gap-3 mt-1">
              <Link
                href="/travel-esim"
                className="group inline-flex items-center gap-2 rounded-full bg-brand-grey px-7 py-3 text-sm 3xl:text-xl font-bold text-brand-bg hover:opacity-90 transition-all"
              >
                Browse all plans
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
              <Link
                href="/register"
                className="inline-flex items-center gap-2 rounded-full border border-brand-stroke px-7 py-3 text-sm 3xl:text-xl font-semibold text-brand-text hover:border-brand-green/30 hover:bg-brand-surface/30 transition-all"
              >
                Create free account
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}
